import update from 'immutability-helper'
import _ from 'lodash'
import { user } from '../utils'
import DataTableUtils from '../components/dataTable/utils'
import { savedSearchesReducer } from './savedSearches'

const SORT_ANALYZER = 'sortAnalyzer'

const dataTable =
  (tableType, fieldDefs) =>
  (state = fieldDefs, action) => {
    switch (action.type) {
      case `ADD_FILTER_CLAUSE_${tableType}`:
        return {
          ...state,
          filterClauses: addFilterClause(
            action.filterClause,
            state.filterClauses
          ),
        }
      case `REPLACE_FILTER_CLAUSES_${tableType}`:
        return { ...state, filterClauses: action.filterClauses }
      case `REMOVE_FILTER_CLAUSE_${tableType}`:
        return {
          ...state,
          filterClauses: removeFilterClause(
            action.filterClause,
            state.filterClauses
          ),
        }
      case `CLEAR_FILTER_CLAUSES_${tableType}`:
        return { ...state, filterClauses: [] }
      case `CHANGE_SORT_BY_${tableType}`:
        return {
          ...state,
          sortBy: formatSortBy(
            action.sortValues,
            action.primaryKeyField,
            action.columns
          ),
        }
      default:
        return state
    }
  }

const removeFilterClause = (filterClause, filterClauses) => {
  const existingFieldIndex = DataTableUtils.getClauseIndex(
    filterClause,
    filterClauses
  )

  if (existingFieldIndex !== -1) {
    return update(filterClauses, { $splice: [[existingFieldIndex, 1]] })
  } else {
    return filterClauses
  }
}

const addFilterClause = (filterClause, filterClauses) => {
  const existingFieldIndex = DataTableUtils.getClauseIndex(
    filterClause,
    filterClauses
  )
  if (existingFieldIndex !== -1) {
    return update(filterClauses, {
      $splice: [[existingFieldIndex, 1, filterClause]],
    })
  } else {
    return [...filterClauses, filterClause]
  }
}

const formatSortBy = (sortValues, primaryKeyField, columns) => {
  const sortedValues = []
  sortValues.forEach(sortValue => {
    const { dataType = 'STRING', field: sortField, sortAscending } = sortValue
    const sortedColumn = _.find(columns, { field: sortValue.field })
    let sortFieldQuery =
      dataType === 'STRING' ? `${sortField}.keyword` : sortField

    if (sortedColumn && SORT_ANALYZER in sortedColumn) {
      const sortAnalyzer = sortedColumn.sortAnalyzer
      sortFieldQuery = `${sortField}${sortAnalyzer}`
    }

    sortedValues.push({
      field: sortFieldQuery,
      order: !sortAscending ? 'desc' : 'asc',
    })
  })

  // Check if sort is already on update_date column
  if (!_.some(sortValues, ['field', 'update_date'])) {
    // If not, check if the grid contains the update_date column and sort by it
    if (
      !primaryKeyField ||
      !columns ||
      _.some(columns, ['field', 'update_date'])
    ) {
      sortedValues.push({
        field: 'update_date',
        order: 'desc',
      })
      // Else, sort by the primary key field
    } else if (primaryKeyField) {
      sortedValues.push({
        field: primaryKeyField,
        order: 'asc',
      })
    }
  }

  return sortedValues
}

const appendRowsReducer = (state, action) => {
  return {
    ...state,
    content:
      action.tableValues.content && action.tableValues.content.length
        ? [...state.content, ...action.tableValues.content]
        : state.content,
    total_elements: state.total_elements + action.tableValues.total_elements,
    isLoading: false,
    isBatchSuccessful: false,
  }
}

const errorReducer = (state, action) => ({
  ...state,
  isLoading: false,
  isBatchSuccessful: false,
  errorInfo: {
    status: action.status,
    statusText: action.statusText,
  },
})

const batchSuccessReducer = (state, action) => ({
  ...state,
  isLoading: false,
  isBatchSuccessful: true,
})

const initialTableState = {
  content: [],
  total_elements: 0,
}

const tableFetchReducer = (tableType, state = initialTableState, action) => {
  switch (action.type) {
    case `RESET_${tableType}`:
      return {
        ...initialTableState,
        isLoading: false,
        isBatchSuccessful: false,
      }
    case `ABORT_${tableType}`:
      return { ...state, isAborted: true, isBatchSuccessful: false }
    case `FETCH_${tableType}`:
      return { ...state, isLoading: true, isBatchSuccessful: false }
    case `STORE_${tableType}`:
      return {
        ...state,
        ...action.tableValues,
        isLoading: false,
        isBatchSuccessful: false,
      }
    case `APPEND_${tableType}`:
      return appendRowsReducer(state, action)
    case `FETCH_FAILURE_${tableType}`:
      return errorReducer(state, action)
    case `BATCH_SUCCESS${tableType}`:
      return batchSuccessReducer(state, action)
    case `SET_LOADING_${tableType}`:
      return { ...state, isLoading: action.isLoading }
    default:
      return state
  }
}

const downloadFetchReducer = (tableType, state = initialTableState, action) => {
  switch (action.type) {
    case `RESET_${tableType}_FOR_DOWNLOAD`:
      return {
        ...initialTableState,
        isLoading: false,
        isBatchSuccessful: false,
      }
    case `ABORT_${tableType}_FOR_DOWNLOAD`:
      return { ...state, isAborted: true, isBatchSuccessful: false }
    case `FETCH_${tableType}_FOR_DOWNLOAD`:
      return { ...state, isLoading: true, isBatchSuccessful: false }
    case `STORE_${tableType}_FOR_DOWNLOAD`:
      return {
        ...state,
        ...action.tableValues,
        isLoading: false,
        isBatchSuccessful: false,
      }
    case `APPEND_${tableType}_FOR_DOWNLOAD`:
      return appendRowsReducer(state, action)
    case `FETCH_FAILURE_${tableType}_FOR_DOWNLOAD`:
      return errorReducer(state, action)
    case `BATCH_SUCCESS${tableType}_FOR_DOWNLOAD`:
      return batchSuccessReducer(state, action)
    default:
      return state
  }
}

const suggestionsFetchReducer = (tableType, state = {}, action) => {
  switch (action.type) {
    case `FETCH_SUGGESTIONS_${tableType}`:
      return { ...state, isLoading: true }
    case `STORE_SUGGESTIONS_${tableType}`:
      const nextSuggestionData = { ...state.suggestionData } // eslint-disable-line no-case-declarations
      _.each(action.suggestionData, fieldSuggestion => {
        nextSuggestionData[fieldSuggestion.field] = fieldSuggestion.values
      })
      return { ...state, suggestionData: nextSuggestionData, isLoading: false }
    default:
      return state
  }
}

const mappedUsersReducer = (tableType, state = {}, action) => {
  switch (action.type) {
    case `STORE_MAPPED_USERS_${tableType}`:
      return { ...state, ...action.mappedUsers }
    default:
      return state
  }
}

const replaceTableUsersInfoReducer = (tableType, state, action) => {
  switch (action.type) {
    case `REPLACE_TABLE_USER_INFO_${tableType}`:
      const { content } = state // eslint-disable-line no-case-declarations
      return {
        ...state,
        content: user.replaceUserIdWithMappedUsersData(
          action.mappedUsers,
          content,
          action.userIdKeys
        ),
      }
    default:
      return state
  }
}

export {
  dataTable,
  tableFetchReducer,
  downloadFetchReducer,
  suggestionsFetchReducer,
  mappedUsersReducer,
  savedSearchesReducer,
  replaceTableUsersInfoReducer,
}
